"use client";

import { useEffect, useState, useRef } from "react";
import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

type TocItem = {
  id: string;
  text: string;
  level: number;
};

export default function SmartSidebarTOC() {
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [backHref, setBackHref] = useState<string>("/");
  const [backLabel, setBackLabel] = useState<string>("Back");

  // ─── Smart Back: kiểm tra referrer ───
  useEffect(() => {
    if (typeof window === "undefined") return;
    const ref = document.referrer;
    if (!ref) return;

    try {
      const refUrl = new URL(ref);
      const currentHost = window.location.host;
      // Cùng domain (jb.com.vn / web.jb.com.vn) và không phải chính trang này
      if (
        refUrl.host.endsWith("jb.com.vn") &&
        refUrl.pathname !== window.location.pathname
      ) {
        setBackHref(refUrl.pathname + refUrl.search);
        setBackLabel("Quay lại");
      }
    } catch {
      // ignore invalid URL
    }
  }, []);

  // ─── Auto generate TOC từ heading ───
  useEffect(() => {
    const collect = () => {
      const headings = Array.from(
        document.querySelectorAll<HTMLHeadingElement>(
          "main h1, main h2, main h3"
        )
      );

      const items: TocItem[] = headings
        .filter((h) => h.textContent && h.offsetParent !== null)
        .map((h, idx) => {
          if (!h.id) {
            h.id = `toc-${idx}-${(h.textContent || "")
              .trim()
              .toLowerCase()
              .replace(/[^\p{L}\p{N}]+/gu, "-")
              .replace(/^-|-$/g, "")
              .slice(0, 50)}`;
          }
          return {
            id: h.id,
            text: (h.textContent || "").trim(),
            level: parseInt(h.tagName.substring(1), 10),
          };
        });

      setTocItems(items);
    };

    // chờ DOM render xong (sections lazy hoặc font load)
    const t = setTimeout(collect, 100);
    return () => clearTimeout(t);
  }, []);

  // ─── Scroll spy: highlight item theo vị trí cuộn ───
  useEffect(() => {
    if (tocItems.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Lấy entries đang ở "vùng cảm" và chọn cái cao nhất
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: "-80px 0px -70% 0px",
        threshold: 0,
      }
    );

    tocItems.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [tocItems]);

  // ─── Click → smooth scroll ───
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const offset = 80;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
    history.replaceState(null, "", `#${id}`);
  };

  return (
    <aside className="hidden xl:block w-56 shrink-0">
      <div className="sticky top-20 space-y-4">
        {/* Back button */}
<Link
  href={backHref}
  className="group inline-flex items-center gap-2.5 px-4 py-3 rounded-xl bg-card border border-border text-[15px] font-semibold text-teal-900 hover:bg-teal-900 hover:text-white hover:border-teal-900 transition-all shadow-card hover:shadow-card-md"
>
  <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-0.5" />
  {backLabel}
</Link>

        {/* TOC */}
        {tocItems.length > 0 && (
          <nav className="border-l border-border">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2 pl-3">
              Mục lục
            </div>
            <ul className="space-y-0.5">
              {tocItems.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    onClick={(e) => handleClick(e, item.id)}
                    className={cn(
                      "block py-1.5 pl-3 pr-2 text-[13px] leading-tight border-l-2 -ml-px transition-all",
                      item.level === 2 && "pl-3",
                      item.level === 3 && "pl-6",
                      activeId === item.id
                        ? "border-orange-500 text-orange-600 font-bold bg-orange-50/50"
                        : "border-transparent text-muted-foreground hover:text-teal-900 hover:border-teal-300"
                    )}
                  >
                    {item.text}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </aside>
  );
}
